/* A tiny observable store. React binds to `version` only: one re-render per tick
   no matter how many fields moved, and the canvas chart can read the same
   snapshot imperatively without going through React at all. */

import { useCallback, useSyncExternalStore } from 'react'

export interface Store<T extends { version: number }> {
  getState: () => T
  setState: (patch: Partial<T> | ((s: T) => Partial<T>)) => void
  subscribe: (listener: () => void) => () => void
}

export function createStore<T extends { version: number }>(initial: T): Store<T> {
  let state = initial
  const listeners = new Set<() => void>()

  return {
    getState: () => state,
    setState(patch) {
      const next = typeof patch === 'function' ? patch(state) : patch
      state = { ...state, ...next, version: state.version + 1 }
      for (const l of listeners) l()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/** Re-renders on every version bump and hands back the whole snapshot. */
export function useStoreSnapshot<T extends { version: number }>(store: Store<T>): T {
  const getVersion = useCallback(() => store.getState().version, [store])
  useSyncExternalStore(store.subscribe, getVersion, getVersion)
  return store.getState()
}
